import React, { useEffect, useMemo, useRef, useState } from "react";
import { CSSTransition } from "react-transition-group";
import { useSelector, useDispatch } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { getTransactions, apiRequestLimits } from "../store/actions/";
import DataLoader from "../components/globalView/DataLoader";
import useWindowSize from "../utils/useWindowSize";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft, faChevronRight, faFileCsv, faSpinner } from "@fortawesome/free-solid-svg-icons";
import TableDataTransactions from "../components/tables/TableDataTransactions";
import ReactPaginate from "react-paginate";
import useDebounce from "../utils/UseDebounce";
import { Helmet } from "react-helmet";
import { useAgencyView } from "../hooks/useAccountType";
import { ppapiPost, apiErrorMessage, dateInputToIso, downloadText } from "../utils/partnerApi";

// Product chips (KOL_CAMPAIGNS_AGENCY_PLAN.md §5). `all` = no filter.
const PRODUCT_CHIPS = [
  { key: "all", label: "All" },
  { key: "swap", label: "Swaps" },
  { key: "split", label: "Splits" },
  { key: "payment", label: "Payments" },
  { key: "spot", label: "Spot" },
  { key: "perps", label: "Perps" },
];

// Collapse split legs under their entry row (same externalCustomerId). The
// entry is the row with the LOWEST id (inserted first) — in a date-desc page
// the payout legs come before it, so "first seen" would pick a leg as parent.
function groupSplitLegs(rows) {
  const out = [];
  const byGroup = new Map();
  for (const r of rows || []) {
    const g = r.externalCustomerId;
    if (!g) { out.push({ item: r, legs: [] }); continue; }
    if (!byGroup.has(g)) { const entry = { item: r, legs: [] }; byGroup.set(g, entry); out.push(entry); continue; }
    const entry = byGroup.get(g);
    if (Number(r.id) < Number(entry.item.id)) { entry.legs.push(entry.item); entry.item = r; }
    else entry.legs.push(r);
  }
  return out;
}

const Transactions = () => {
  const dispatch = useDispatch();
  const sized = useWindowSize();
  const agencyView = useAgencyView();
  const [searchParams, setSearchParams] = useSearchParams();

  const [currentPage] = useState(1);
  const [tableOpen, setTableOpen] = useState("");
  const [transaction, setTransaction] = useState();
  const [modalIsOpen, setIsOpen] = useState(false);
  const [openGroups, setOpenGroups] = useState({}); // externalCustomerId -> legs shown

  // Filters: product chip, campaign (code | 'direct'), date range. Campaign is
  // deep-linkable (?campaign=) from Campaign Stats.
  const [product, setProduct] = useState("all");
  const [campaign, setCampaign] = useState(searchParams.get("campaign") || "");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [campaigns, setCampaigns] = useState([]);
  const [exporting, setExporting] = useState(false);
  const filters = useMemo(() => ({
    product: product !== "all" ? product : undefined,
    campaign: campaign || undefined,
    from: dateInputToIso(from),
    to: to ? `${to}T23:59:59Z` : undefined,
  }), [product, campaign, from, to]);

  const transactions = useSelector((state) => state.userReducer.transactions);
  const isLoading = useSelector((state) => state.userReducer.loading);
  const customLoading = useSelector((state) => state.userReducer.customLoader);
  const pageSize = useSelector((state) => state.userReducer.transactions.pages);
  const transactionsArray =
    transactions &&
    Object.keys(transactions).length > 0 &&
    transactions.transactions;
  const grouped = useMemo(() => groupSplitLegs(Array.isArray(transactionsArray) ? transactionsArray : []), [transactionsArray]);
  // Chips only when ppapi returns the product columns — an older ppapi ignores the
  // `product` filter, so showing chips would look like a filter that does nothing.
  const hasProductData = useMemo(() => (Array.isArray(transactionsArray) ? transactionsArray : []).some((r) => r.walletType != null), [transactionsArray]);
  // useDebounce memoises the callback once — read the live filters through a ref.
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  // First load: full-page loader (as before). Filter changes: inline loader
  // (page 1) so the filter inputs stay mounted and keep focus.
  const firstLoadRef = useRef(true);
  useEffect(() => {
    dispatch(getTransactions(firstLoadRef.current ? null : 1, 8, filters));
    if (firstLoadRef.current) { dispatch(apiRequestLimits()); firstLoadRef.current = false; }
  }, [dispatch, filters, agencyView?.id]);

  useEffect(() => {
    ppapiPost("listCampaigns", {}, { view: true })
      .then((d) => setCampaigns(Array.isArray(d?.campaigns) ? d.campaigns : []))
      .catch(() => setCampaigns([]));
  }, [agencyView?.id]);

  const changeCampaign = (v) => {
    setCampaign(v);
    const next = new URLSearchParams(searchParams);
    if (v) next.set("campaign", v); else next.delete("campaign");
    setSearchParams(next, { replace: true });
  };

  const exportCsv = () => {
    setExporting(true);
    ppapiPost("transactionsExport", { ...filters }, { view: true })
      .then((d) => {
        const csv = typeof d === "string" ? d : d?.csv;
        if (!csv) { toast.error("CSV export is not enabled on your account yet."); return; }
        downloadText(d?.filename || `swap-history_${from || "start"}_${to || "today"}${campaign ? `_${campaign}` : ""}.csv`, csv);
        if (d?.truncated) toast.warn(`Export capped at ${Number(d.rows).toLocaleString()} rows — narrow the date range for the rest.`);
      })
      .catch((e) => toast.error(apiErrorMessage(e, "Export is not available yet.")))
      .finally(() => setExporting(false));
  };

  const currentDiv = (id) => {
    setTableOpen(id);
  };

  const getTransDebounced = useDebounce((number) => {
    dispatch(getTransactions(number, 8, filtersRef.current));
  }, 200);

  function handlePageClick({ selected: selectedPage }) {
    getTransDebounced(selectedPage + 1);
  }

  const handlePrevClick = () => {
    getTransDebounced(currentPage - 1);
  };

  const handleNextClick = () => {
    getTransDebounced(currentPage + 1);
  };

  const openModal = (value, item) => {
    setIsOpen(value);
    setTransaction(item);
  };

  const btnPrev = (
    <button
      style={
        pageSize === 1
          ? { display: "none", outline: "none" }
          : { display: "block", outline: "none" }
      }
      className="btn-pagination"
      disabled={currentPage && currentPage <= 1}
      onClick={handlePrevClick}
    >
      <FontAwesomeIcon icon={faChevronLeft} />
    </button>
  );

  const btnNext = (
    <button
      style={
        pageSize === 1
          ? { display: "none", outline: "none" }
          : { display: "block", outline: "none" }
      }
      className="btn-pagination"
      disabled={currentPage && currentPage === pageSize}
      onClick={handleNextClick}
    >
      <FontAwesomeIcon icon={faChevronRight} />
    </button>
  );

  const isDesktop = sized.width > 1024;

  return (
    <>
      <Helmet>
        <title>Transactions | Instaswap Partners</title>
      </Helmet>
      {isLoading ? (
        <DataLoader />
      ) : (
        <CSSTransition
          timeout={{
            enter: 8500,
            exit: 500,
          }}
          classNames="item"
          key="transactions"
        >
          <div>
            <div className="secHdr" style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
              <div>
                <h2 className="secTitle">Swap History</h2>
                <p className="secLead">Every order routed through InstaSwap with your attribution — swaps, splits, payments, spot and perps.</p>
              </div>
              <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
                {campaigns.length > 0 && (
                  <div>
                    <label className="fldLbl" style={{ fontSize: 11 }}>Campaign</label>
                    <select className="input" value={campaign} onChange={(e) => changeCampaign(e.target.value)} style={{ height: 38, fontSize: 12.5, minWidth: 170 }}>
                      <option value="">All campaigns</option>
                      <option value="direct">Direct (no campaign)</option>
                      {campaigns.map((c) => <option key={c.code} value={c.code}>{c.label} · {c.code}</option>)}
                    </select>
                  </div>
                )}
                <div>
                  <label className="fldLbl" style={{ fontSize: 11 }}>From</label>
                  <input className="input" type="date" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} style={{ height: 38, fontSize: 12.5, width: 150 }} />
                </div>
                <div>
                  <label className="fldLbl" style={{ fontSize: 11 }}>To</label>
                  <input className="input" type="date" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} style={{ height: 38, fontSize: 12.5, width: 150 }} />
                </div>
                <button type="button" className="btnGhost sm" onClick={exportCsv} disabled={exporting} style={{ height: 38 }} title="Export the current filters as CSV">
                  {exporting ? <FontAwesomeIcon icon={faSpinner} spin /> : <><FontAwesomeIcon icon={faFileCsv} /> CSV</>}
                </button>
              </div>
            </div>

            {(hasProductData || product !== "all") && (
              <div className="seg" style={{ display: "inline-flex", marginBottom: 16, flexWrap: "wrap" }}>
                {PRODUCT_CHIPS.map((c) => (
                  <button key={c.key} type="button" className={`segB${product === c.key ? " on" : ""}`} onClick={() => setProduct(c.key)}>{c.label}</button>
                ))}
              </div>
            )}

            {transactions && Object.keys(transactions).length > 0 ? (
              customLoading ? (
                <div style={{ minHeight: "50vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <DataLoader />
                </div>
              ) : isDesktop ? (
                <div className="gtScroll">
                  <div style={{ minWidth: 980 }}>
                    <div
                      className="gtHead"
                      style={{
                        gridTemplateColumns:
                          "110px minmax(220px,1.5fr) minmax(180px,1.2fr) 120px minmax(150px,1fr) 170px",
                      }}
                    >
                      <span>ID</span>
                      <span>Pair</span>
                      <span>Profit</span>
                      <span>Status</span>
                      <span>Tx Hash</span>
                      <span>Date</span>
                    </div>
                    {grouped.map(({ item, legs }) => (
                      <React.Fragment key={item.id}>
                        <TableDataTransactions
                          item={item}
                          tableOpen={tableOpen}
                          currentDiv={currentDiv}
                          openModal={(open) => openModal(open, item)}
                        />
                        {legs.length > 0 && (
                          <div style={{ padding: "4px 14px 8px", borderBottom: "1px solid rgba(255,255,255,.06)" }}>
                            <button type="button" className="btnGhost sm" onClick={() => setOpenGroups((g) => ({ ...g, [item.externalCustomerId]: !g[item.externalCustomerId] }))}>
                              {openGroups[item.externalCustomerId] ? "Hide" : "Show"} {legs.length} payout leg{legs.length > 1 ? "s" : ""}
                            </button>
                            {openGroups[item.externalCustomerId] && (
                              <div style={{ marginTop: 6, opacity: 0.85 }}>
                                {legs.map((leg) => (
                                  <TableDataTransactions key={leg.id} item={leg} tableOpen={tableOpen} currentDiv={currentDiv} openModal={(open) => openModal(open, leg)} />
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              ) : (
                <div>
                  {grouped.map(({ item, legs }) => (
                    <React.Fragment key={item.id}>
                      <TableDataTransactions
                        item={item}
                        tableOpen={tableOpen}
                        currentDiv={currentDiv}
                        openModal={(open) => openModal(open, item)}
                      />
                      {legs.map((leg) => (
                        <TableDataTransactions key={leg.id} item={leg} tableOpen={tableOpen} currentDiv={currentDiv} openModal={(open) => openModal(open, leg)} />
                      ))}
                    </React.Fragment>
                  ))}
                </div>
              )
            ) : (
              <div className="gtScroll">
                <div className="gtEmpty">{product !== "all" || campaign || from || to ? "No orders match these filters" : "No transactions yet"}</div>
              </div>
            )}

            {transactions &&
              Object.keys(transactions).length > 0 &&
              pageSize &&
              pageSize > 1 && (
                <div
                  className="pagination"
                  style={{ display: "flex", justifyContent: "center", padding: "22px 0" }}
                >
                  <ReactPaginate
                    previousLabel={btnPrev}
                    nextLabel={btnNext}
                    breakLabel={"..."}
                    disableInitialCallback={false}
                    breakClassName={"break-me"}
                    pageCount={pageSize}
                    hrefBuilder={() => currentPage + 1}
                    marginPagesDisplayed={2}
                    onPageChange={handlePageClick}
                    forcePage={currentPage - 1}
                    subContainerClassName={"pages pagination"}
                    activeClassName={"active"}
                  />
                </div>
              )}
          </div>
        </CSSTransition>
      )}
    </>
  );
};

export default Transactions;
